import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Icon } from "@/components/kit";
import { ProFooter } from "@/components/prospect";
import { categories } from "@/theme";
import { api, proLink } from "@/api";
import { useI18n } from "@/i18n";

export default function DevisPro() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [pro, setPro] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | notfound

  useEffect(() => {
    let done = false;
    (async () => {
      try {
        const p = await api.publicPro(slug);
        if (done) return;
        setPro(p);
        proLink.set(slug);
        setStatus("ok");
      } catch (e) {
        proLink.clear();
        if (!done) setStatus("notfound");
      }
    })();
    return () => { done = true; };
  }, [slug]);

  if (status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper">
        <Icon name="loader" size={36} className="animate-spin text-brand" />
      </div>
    );
  }
  if (status === "notfound") return <Navigate to="/" replace />;

  return (
    <div className="flex min-h-screen flex-col bg-paper">
      {/* Branded header (pro's own logo & company name) */}
      <header className="border-b border-line bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-5 py-4">
          <div className="flex min-w-0 items-center gap-3">
            {pro?.logo_url ? (
              <img data-testid="pro-logo" src={pro.logo_url} alt={pro.company_name || ""} className="h-12 max-w-[160px] object-contain" />
            ) : null}
            <span data-testid="pro-company-name" className="truncate text-[16px] font-semibold text-ink">{pro?.company_name}</span>
          </div>
          <LanguageSwitcher />
        </div>
      </header>

      {/* Body: quote request form (category selection) */}
      <main className="mx-auto w-full max-w-6xl flex-1 px-5 py-10">
        <h1 className="font-display text-4xl font-semibold text-ink sm:text-5xl">{t("services_title")}</h1>
        <p className="mb-8 mt-2 max-w-2xl text-[15px] text-mute">{t("hero_sub")}</p>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {categories.map((c, i) => (
            <button
              key={c.key}
              type="button"
              data-testid={`category-${c.key}`}
              onClick={() => navigate(`/quote/${c.key}`)}
              style={{ animationDelay: `${i * 70}ms` }}
              className="group relative flex h-56 animate-fade-up flex-col justify-end overflow-hidden rounded-2xl text-left"
            >
              <img src={c.image} alt="" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2a2624]/90 to-transparent" />
              <span className="absolute left-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 backdrop-blur">
                <Icon name={c.icon} size={18} className="text-ink" />
              </span>
              <div className="relative p-4">
                <p className="text-[16px] font-semibold text-white">{t(`cat_${c.key}`)}</p>
                <p className="mt-0.5 text-xs text-white/80">{t(`cat_${c.key}_short`)}</p>
              </div>
            </button>
          ))}
        </div>

        <div className="mt-8 flex items-start gap-3 rounded-2xl bg-brand-tertiary p-5">
          <Icon name="zap" size={22} className="mt-0.5 shrink-0 text-brand" />
          <div>
            <p className="text-[15px] font-semibold text-brand-on">{t("ai_info_title")}</p>
            <p className="mt-0.5 text-[13px] leading-relaxed text-ink-3">{t("ai_info_text")}</p>
          </div>
        </div>
      </main>

      {/* Legal line + CGV / RGPD du pro */}
      <footer className="border-t border-line px-5 py-5">
        <ProFooter pro={pro} />
      </footer>
    </div>
  );
}
